<template lang="html">
  <div :class="charger ? 'charging' : 'start-charging'">
    <loading-layout
      v-show="!successfullLoaded"
      :loading-text="$t('chargingPage.loadingText')"
    />

    <connector-layout
      v-show="successfullLoaded && !charger"
      :start-call-is-loading="startCallIsLoading"
      @connectorchanged="connectorChanged"
    />

    <charging-layout
      v-if="charger"
      :selected-charger="charger"
      :charging-started-at="chargingStartedAt"
    />
    <feedback-modal
      v-if="isFeedbackModalActive"
      :main-title="errorModalTitles.main"
      :sub-title="errorModalTitles.sub"
      :success="false"
      @confirm="handleFeedbackModalOkClick"
    />
  </div>
</template>

<script lang="ts" setup>
import LoadingLayout from "@/components/layout/LoadingLayout"
import ChargingLayout from "@/components/layout/ChargingLayout"
import ConnectorLayout from "@/components/layout/ConnectorLayout"
import FeedbackModal from "@/components/ui/FeedbackModal"
import { GuestChargingService } from "@/services/guest-charging-service"
import { AuthService } from "@/services/auth-service"
import { computed, onBeforeMount, reactive, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useDictionaryStore } from "../store/dictionary"
import { storeToRefs } from "pinia"
import { useUserStore } from "../store/user"
import { ChargingStatus } from "@/models/enums"
import type { Charger } from "@/models/common"

const { locale, t } = useI18n()
const router = useRouter()
const { setTempToken, setBearerToken } = useUserStore()
const dictionaryStore = useDictionaryStore()
const { dynamicConfig } = storeToRefs(dictionaryStore)

interface ChargingPageProps {
  temporaryCode: string
}

const props = withDefaults(defineProps<ChargingPageProps>(), {
  temporaryCode: ""
})

const emit = defineEmits(["bv::show::modal"])

const successfullLoaded = ref(false)
const selectedCharger = ref<Charger | null>(null)
const charger = ref<Charger | null>(null)
const isFeedbackModalActive = ref(false)
const startCallIsLoading = ref(false)
const authenticateData = ref<{ chargingStartedAt: string } | null>(null)
const errorModalTitles = reactive({
  main: "",
  sub: ""
})

const chargingStartedAt = computed(
  () =>
    authenticateData.value?.chargingStartedAt &&
    new Date(authenticateData.value.chargingStartedAt)
)

const feedbackStartError = () => {
  console.error("ChargingPage feedbackStartError")
  errorModalTitles.main = t("errors.chargingErrors.startError.mainTitle")
  errorModalTitles.sub = t("errors.chargingErrors.startError.subTitle")
  isFeedbackModalActive.value = true
}

const start = async () => {
  startCallIsLoading.value = true
  try {
    const evseId = selectedCharger?.value?.connector?.evseId
    if (evseId) await GuestChargingService.startCharging(evseId)
    charger.value = selectedCharger.value
  } catch (e) {
    console.error(e)
    feedbackStartError()
  } finally {
    startCallIsLoading.value = false
  }
}

const connectorChanged = (obj: Charger) => {
  selectedCharger.value = obj
  start()
}

const selectedChargerTransform = (chargingInfo: any) => {
  charger.value = {
    connector: chargingInfo,
    address: chargingInfo.stationAddress
  }
}

const navigateToRedirectUrl = () => {
  if (dynamicConfig?.value?.redirectUrl) {
    window.location.href = dynamicConfig.value.redirectUrl
  } else {
    router.push({ name: "LandingPage" })
  }
}

const processChargingInfo = (response: any) => {
  if (!response?.data) {
    throw new Error("processChargingInfo: Response is null!")
  }

  const { connectorInfo, chargingStatus } = response.data

  const actions: Partial<Record<ChargingStatus, () => void>> = {
    [ChargingStatus.STARTED]: () => selectedChargerTransform(connectorInfo),
    [ChargingStatus.PENDING]: () => selectedChargerTransform(connectorInfo),
    [ChargingStatus.START_COMMAND_ACCEPTED]: () =>
      selectedChargerTransform(connectorInfo),
    [ChargingStatus.STOPPED]: () => navigateToRedirectUrl(),
    [ChargingStatus.INVALID]: () => {
      errorModalTitles.main = t("errors.chargingStatusErrors.invalid.mainTitle")
      errorModalTitles.sub = t("errors.chargingStatusErrors.invalid.subTitle")
      emit("bv::show::modal", "errorFeedbackModal")
    }
  }

  actions[chargingStatus as ChargingStatus]?.()

  successfullLoaded.value = true
}

const setLanguagePreference = (response: any) => {
  locale.value = response?.data?.languagePreference ?? locale.value
}

const authenticate = async () => {
  try {
    const resp = await AuthService.getJwtToken(props.temporaryCode)
    authenticateData.value = resp.data
    processChargingInfo(resp)
    setLanguagePreference(resp)
  } catch (e: any) {
    console.error(e)

    const messageCode = e.response?.data?.messageCode

    const errorMap: Record<string, { main: string; sub: string }> = {
      "guestCharging.session.temporaryCodeExpired": {
        main: "errors.chargingErrors.temporaryCodeExpired.mainTitle",
        sub: "errors.chargingErrors.temporaryCodeExpired.subTitle"
      },
      "guestCharging.public.temporaryCodeNotFound": {
        main: "errors.chargingErrors.temporaryCodeNotFound.mainTitle",
        sub: "errors.chargingErrors.temporaryCodeNotFound.subTitle"
      }
    }

    const { main, sub } = errorMap[messageCode] || {
      main: "errors.chargingErrors.authError.mainTitle",
      sub: "errors.chargingErrors.authError.subTitle"
    }

    errorModalTitles.main = t(main)
    errorModalTitles.sub = t(sub)

    isFeedbackModalActive.value = true
    setTempToken("")
    setBearerToken("")
  }
}

const handleFeedbackModalOkClick = () => {
  navigateToRedirectUrl()
  isFeedbackModalActive.value = false
}

onBeforeMount(() => {
  setTempToken(props.temporaryCode)
  authenticate()
})
</script>
